import React from "react";
import { Activity, SurveyReportBuilder, SurveyReportDataSet, fieldValidation, getSurveyStatus, rng } from "../../../../../../amorg-common";
import { ActionPlanListView } from "../../../../../../amorg-common/DTOs/ActionPlanListView";
import { deleteActivity, updateActivity } from "../../../../../../api/activityClientApi";
import { getActionPlansBySurveyId } from "../../../../../../api/actionPlanClientApi";
import { createSurvey } from "../../../../../../api/surveyClientApi";
import { Button } from "../../../../../../designSystem/button/Button";
import { IconWithText } from "../../../../../../designSystem/compositeComponents/compositeIcons/IconWithText";
import { Flex } from "../../../../../../designSystem/compositeComponents/FlexHelpers";
import { LinkWithCopyButton } from "../../../../../../designSystem/compositeComponents/LinkWithCopyButton";
import { TitleContainer } from "../../../../../../designSystem/compositeComponents/TitleContainer";
import { DownloadableQrCode } from "../../../../../../designSystem/DownloadableQrCode";
import { LoadingPageOverlay } from "../../../../../../designSystem/LoadingPageOverlay";
import { SimpleStatCard, StatCard, StatCardRow } from "../../../../../../designSystem/StatCard";
import { timeToClose, timeToOpen } from "../../../../../../utils/date";
import { SurveyActionPlanStatusSection } from "../_components/SurveyActionPlanStatusSection";
import { WorkplaceAssessmentComponentProps } from "../_components/WorkplaceAssessmentComponentProps";
import { DeleteConfirmationButton } from "../../../../../../designSystem/compositeComponents/DeleteConfirmationButton";
import { useNavigate } from "react-router-dom";
import { SingleColumnLayout } from "../../../../../_layouts/SingleColumnLayout";
import { PageHeader } from "../../../../../../designSystem/PageHeader";
import { EditableTextArea } from "../../../../../../designSystem/compositeComponents/EditableTextArea";
import { FormField } from "../../../../../../designSystem/formField/FormField";
import { StartEndDateFields } from "../../../../../../designSystem/inputs/StartEndDateFields";
import { PageSection } from "../../../../../../designSystem/PageSection";
import { Card } from "../../../../../../designSystem/Card";
import { Label } from "../../../../../../designSystem/Label";
import { SimpleReportSummarySection } from "../_components/SimpleReportSummarySection";
import { CountAvatar } from "../../../../../../designSystem/avatar/CountAvatar";



type State = {
    data: SurveyReportDataSet | null,
    actionPlans: ActionPlanListView[];
};
export function OverviewPanel (props: WorkplaceAssessmentComponentProps): JSX.Element
{
    const [state, setState] = React.useState<State>(
        {
            data: null,
            actionPlans: []
        });

    const navigate = useNavigate();

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            if (props.surveyLoading)
            {
                setState({ data: null, actionPlans: [] });
            }
            else if (props.survey && props.surveyReport)
            {
                const builder = new SurveyReportBuilder(props.survey, props.surveyReport);
                setState({
                    data: builder.getData(),
                    actionPlans: await getActionPlansBySurveyId(props.survey.id)
                });
            }
        };
        getData();

    }, [props.survey, props.surveyReport, props.surveyLoading]);

    async function handleClickDeleteActivity ()
    {
        await deleteActivity(props.activity.id);
        navigate('/workplaceassessments');
    }

    async function handleClickCreateSurvey ()
    {
        const id = rng.getRandomId();
        const result = await createSurvey(id, props.activity.id);
        if (result.isSuccess)
        {
            props.onSurveyUpdated();
        }
    }

    async function performUpdate (mutator: (activity: Activity) => void)
    {
        mutator(props.activity);
        await updateActivity(
            props.activity.id,
            props.activity.title,
            props.activity.description,
            props.activity.start,
            props.activity.end,
            props.activity.location
        );
        props.onActivityUpdated();
    }

    async function updateTitle (value: string)
    {
        await updateActivity(props.activity.id, value, props.activity.description, props.activity.start, props.activity.end, props.activity.location);
        props.onActivityUpdated();
    }

    let openStatus: 'waiting' | 'open' | 'closed' = 'waiting';
    if (props.survey)
    {
        if (props.survey.start > new Date())
            openStatus = 'waiting';
        else
        {
            if (props.survey.end >= new Date())
                openStatus = 'open';
            else
                openStatus = 'closed';
        }
    }

    const canSubmitSurveyAnswer = props.survey ? getSurveyStatus(props.survey).canSubmitAnswer : false;
    const surveyAnswerUrl = `${window.location.protocol}//${window.location.host}/surveys/${props.survey?.id || ''}@${props.activeUser.siteId}`;

    const titleIndent = { marginLeft: '16px', marginRight: '16px' };

    return (
        <>
            <PageHeader
                noDivider
                noTopMargin
                noBottomMargin
                headerStyle="secondary-gray"
                headerText="APV - oversigt"
                buttons={
                    <DeleteConfirmationButton
                        variant="destructive-text"
                        buttonText={""}
                        confirmationHeading={"Slet APVen"}
                        confirmationText={"Er du sikker på at du vil slette APVen ? Spørgeskemaet, besvarelser og handleplaner vil også blive slettet. Handlingen kan IKKE fortrydes."}
                        requireKey={true}
                        onConfirmDelete={handleClickDeleteActivity}
                    />
                }
            />

            <SingleColumnLayout>
                <LoadingPageOverlay loading={!props.activity || props.surveyLoading} />

                <PageSection contentStyleVariant='card'>

                    <Flex flexDirection="column" gap={32}>

                        <Flex gap={16}>

                            <TitleContainer title={props.activity.title} updateTitle={updateTitle} />

                            <Flex style={{ width: '600px' }}>
                                <FormField
                                    label={"Periode"}
                                    inputComponent={
                                        <StartEndDateFields
                                            start={props.activity.start}
                                            end={props.activity.end}
                                            onChanged={(start, end) => performUpdate(a =>
                                            {
                                                a.start = start;
                                                a.end = end;
                                            })}
                                        />
                                    } />
                            </Flex>

                        </Flex>


                        <div style={titleIndent}>
                            <FormField
                                label="Beskrivelse af undersøgelsen"
                                inputComponent={
                                    <EditableTextArea
                                        ariaLabel="Aktivitetsbeskrivelse"
                                        placeholder="Beskrivelse..."
                                        value={props.activity.description}
                                        onSave={newValue =>
                                        {
                                            if (fieldValidation.activity.description(newValue))
                                                performUpdate((a) => { a.description = newValue; });
                                        }}
                                        validator={(currentValue) =>
                                            fieldValidation.activity.description(currentValue)
                                                ? { valid: true }
                                                : {
                                                    valid: false, errorMessage: `Ugyldig beskrivelse. Maks. 10000 tegn (${currentValue.length}).`
                                                }}
                                    />
                                }
                            />
                        </div>

                        {props.survey &&
                            <LinkPanel url={surveyAnswerUrl} canSubmitSurveyAnswer={canSubmitSurveyAnswer} />
                        }
                    </Flex>

                </PageSection>


                {props.activity &&
                    <>

                        <Flex flexDirection="column" gap={16}>
                            {props.survey &&

                                <StatCardRow>

                                    <StatCard header="Status">

                                        <Flex flexDirection="column" gap={8}>

                                            {openStatus === 'waiting' &&
                                                <>
                                                    <IconWithText icon="interface_glyph_hourglass" variant="passive">
                                                        <span>Spørgeskemaet åbner snart</span>
                                                    </IconWithText>

                                                    <IconWithText icon="clock" variant="warning">
                                                        <span>{timeToOpen(props.survey.start, new Date())}</span>
                                                    </IconWithText>
                                                </>
                                            }

                                            {openStatus === 'open' &&
                                                <>
                                                    <IconWithText icon="lock_open" variant="success">
                                                        <span>Spørgeskemaet er åbent</span>
                                                    </IconWithText>
                                                    <IconWithText icon="clock" variant="warning">
                                                        <span>{timeToClose(props.survey.end, new Date())}</span>
                                                    </IconWithText>
                                                </>
                                            }

                                            {openStatus === 'closed' &&
                                                <>
                                                    <IconWithText icon="lock" variant="error">
                                                        <span>Spørgeskemaet er lukket</span>
                                                    </IconWithText>
                                                </>
                                            }
                                        </Flex>

                                    </StatCard>


                                    <StatCard header="Undersøgelsens opbygning">
                                        <Flex flexDirection="column" gap={8} alignItems="flex-start">
                                            <Flex gap={4}>
                                                <CountAvatar count={props.survey.themes.flatMap(t => t.questions).length} /> <span>Spørgsmål</span>
                                            </Flex>
                                            <Flex gap={4}>
                                                <CountAvatar count={props.survey.themes.length} /> <span>Temaer</span>
                                            </Flex>
                                        </Flex>

                                    </StatCard>


                                    <SimpleStatCard header="Besvarelser"
                                        subText={props.survey.finishedAnswerCount == props.survey.answerCount
                                            ? 'Alle afsluttet'
                                            : `${props.survey.finishedAnswerCount} afsluttet`
                                        }
                                        stat={props.survey.answerCount.toString()}
                                    />

                                </StatCardRow>
                            }

                            {!props.survey &&
                                <>
                                    <Card variant="outlined">
                                        <Flex flexDirection="column" gap={16} justifyContent="center">
                                            <IconWithText icon="exclamation" variant="active" style={{ alignSelf: 'center' }}>
                                                Der er ikke oprettet et spørgeskema for APVen
                                            </IconWithText>
                                            <Flex justifyContent="center">
                                                <Button
                                                    variant='light'
                                                    size={"42px"}
                                                    onClick={handleClickCreateSurvey}
                                                    text={"Opret spørgeskema"}
                                                    icon="actions_plus"
                                                />
                                            </Flex>
                                        </Flex>
                                    </Card>
                                </>


                            }
                        </Flex>

                        {props.survey && props.surveyReport && state.data &&
                            <>

                                <SimpleReportSummarySection survey={props.survey} data={state.data} />

                                <SurveyActionPlanStatusSection headerText="Status for oprettelse af handleplaner" survey={props.survey} surveyReport={props.surveyReport} actionPlans={state.actionPlans} />
                            </>
                        }
                    </>
                }
            </SingleColumnLayout>
        </>
    );
}

function LinkPanel ({ url, canSubmitSurveyAnswer }: { url: string, canSubmitSurveyAnswer: boolean; })
{
    return (
        <PageSection
            header={{
                title: 'Opretttelse af besvarelser'
            }}
            headerStyleVariant="card"
            contentStyleVariant="card">

            <Flex flexDirection="column" gap={16}>

                <Flex gap={32}>
                    <Flex flexDirection="column" gap={16}>
                        <Flex flexDirection="column" gap={8} style={{ flex: 1 }}>
                            <Label>Del undersøgelsen med jeres medarbejdere</Label>
                            <div style={{ fontSize: '14px' }}>
                                Når medarbejderne tilgår linket eller scanner QR-koden, kan de indtaste deres e-mail hvorefter et link til deres personlige besvarelse sendes til dem.
                                Rapporten opdateres løbende med medarbejdernes besvarelser.
                            </div>
                        </Flex>


                        <Card variant="lightGray" padding="8px">
                            <LinkWithCopyButton
                                disabled={!canSubmitSurveyAnswer}
                                url={url}
                                buttonText="Kopiér link"
                                buttonVariant="outlined"
                            />
                        </Card>
                    </Flex>

                    <Card variant="lightGray" style={{ height: 'min-content' }}>
                        <DownloadableQrCode
                            disabled={!canSubmitSurveyAnswer}
                            url={url}
                            fileName="qr-kode-spoergeskema.svg"
                        />
                    </Card>
                </Flex>
            </Flex>
        </PageSection>
    );
}
